import React, { useEffect, useMemo, useState, useRef } from "react";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Progress } from "./ui/progress";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { ArrowLeft, Play, Volume2, HelpCircle, Menu, X, Mic, Square } from "lucide-react";
import { QuestionPalette } from "./ui/QuestionPalette";
import { Word, Phoneme } from "../data/words";
import { getSpeakingSentences, submitSpeakingBatch } from "../services/api";
import login_hero_image from '../assets/eko-_transparent-removebg-preview.png';
import TestBadge from './TestBadge';

/* ----------------------------- Main Interface ----------------------------- */
type ResultRow = {
  word: string;
  user_input: string;
  type: string;
  time: number;
  hints_used: number;
};

interface Sentence {
  sentence_id: string;
  sentence: string;
  word_count: number;
  difficulty: string;
  audio_base64: string;
}

interface SpeakingSubmission {
  sentence_id: string;
  original_sentence: string;
  audio_base64: string;
  audio_format: string;
  // How long the child sat before starting to read. Only the client can know
  // this: it is the gap between pressing record and the first sound, and it
  // is gone by the time the audio reaches the server.
  time_to_speak_ms: number | null;
  // Retakes are signal. The last attempt measures ability; the count measures
  // confidence.
  attempt: number;
}

interface SpeakingTestInterfaceProps {
  words: Word[];
  onComplete: (results: { words: ResultRow[] }) => void;
  onBackToDashboard: () => void;
  playerName: string;
  gradeName: string;
  childId: string;
}

export const SpeakingTestInterface: React.FC<SpeakingTestInterfaceProps> = ({
  words,
  onComplete,
  onBackToDashboard,
  playerName,
  gradeName,
  childId,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [results, setResults] = useState<ResultRow[]>([]);
  const [attemptedIndices, setAttemptedIndices] = useState<number[]>([]);
  const [skippedIndices, setSkippedIndices] = useState<number[]>([]);
  const [isComplete, setIsComplete] = useState(false);
  const [lastResult, setLastResult] = useState<'correct' | 'incorrect' | null>(null);
  const [startTime, setStartTime] = useState<number>(Date.now());
  const [currentWordTime, setCurrentWordTime] = useState<number>(0);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  
  // Speaking test specific states
  const [sentences, setSentences] = useState<Sentence[]>([]);
  const [recordings, setRecordings] = useState<Map<string, string>>(new Map());
  const [isRecording, setIsRecording] = useState(false);
  // The recording is encoded asynchronously after MediaRecorder stops. Until
  // that finishes there is no audio to submit, so anything that can advance
  // the test has to wait for it.
  const [isProcessingAudio, setIsProcessingAudio] = useState(false);
  const [recordingError, setRecordingError] = useState<string | null>(null);
  // sentence_id -> ms between pressing record and the first sound.
  const [timeToSpeak, setTimeToSpeak] = useState<Map<string, number>>(new Map());
  // sentence_id -> how many times this sentence has been recorded.
  const [attempts, setAttempts] = useState<Map<string, number>>(new Map());
  const [isLoadingSentences, setIsLoadingSentences] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  // Resolves once the recording has been encoded and stored, or rejected.
  const encodingRef = useRef<Promise<void> | null>(null);

  // A recording has to contain speech. These mirror the server-side audio
  // gate so the child is told now, rather than seeing a zero in the report.
  const MIN_RECORDING_SECONDS = 0.35;
  // The loudest part of any real recording, however far back the child sits.
  const MIN_PEAK_FRAME_RMS = 0.0008;
  // Speech is loud on vowels and near-silent between words; steady noise is
  // not. Thresholding that spread rather than the volume is what lets a quiet
  // child through - an absolute cut-off rejected readings the server went on
  // to score 98.
  const MIN_DYNAMIC_RANGE = 6.0;
  const MIN_VOICED_FRACTION = 0.08;
  const VOICED_RELATIVE_TO_PEAK = 0.15;
  const FRAME_SECONDS = 0.03;

  const currentSentence = sentences[currentIndex];
  const totalWords = sentences.length;

  const progressPercentage = useMemo(
    () => ((currentIndex + 1) / totalWords) * 100,
    [currentIndex, totalWords]
  );

  // Motivational messages that rotate across the test
  const motivationalMessages = useMemo(() => [
    `Great start, {name}!`,
    `Awesome work, {name}!`,
    `Keep going, {name}!`,
    `You're doing great, {name}!`
  ], []);

  const firstName = useMemo(() => (playerName?.split(" ")[0] || playerName || ""), [playerName]);

  const currentMotivation = useMemo(() => {
    if (!totalWords || totalWords <= 0) return motivationalMessages[0].replace('{name}', firstName);
    const segments = motivationalMessages.length;
    const segmentSize = Math.max(1, Math.floor(totalWords / segments));
    const idx = Math.min(segments - 1, Math.floor(currentIndex / segmentSize));
    return motivationalMessages[idx].replace('{name}', firstName);
  }, [currentIndex, totalWords, motivationalMessages, firstName]);

  /* ----------------------------- Fix: Mobile / Tablet detection ----------------------------- */
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 768 && window.innerWidth < 1024);
  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      setIsMobile(w < 768);
      setIsTablet(w >= 768 && w < 1024);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* ----------------------------- Effects ----------------------------- */
  // Fetch sentences on mount
  useEffect(() => {
    const fetchSentences = async () => {
      try {
        setIsLoadingSentences(true);
        const data = await getSpeakingSentences(childId, gradeName);
        setSentences(data.sentences || []);
      } catch (error) {
        console.error('Error fetching sentences:', error);
        alert('Failed to load sentences. Please try again.');
      } finally {
        setIsLoadingSentences(false);
      }
    };
    
    fetchSentences();
  }, [childId, gradeName]);

  useEffect(() => {
    setIsComplete(false);
    // Reset timer for new sentence
    setStartTime(Date.now());
    setCurrentWordTime(0);
  }, [currentIndex]);

  // Timer effect to track current word time
  useEffect(() => {
    if (isComplete) return;

    const timer = setInterval(() => {
      setCurrentWordTime(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    return () => clearInterval(timer);
  }, [startTime, isComplete]);

  /* ----------------------------- Handlers ----------------------------- */
  // Helper function to find next unattempted or skipped question (prioritizing skipped first)
  const findNextUnattemptedOrSkippedQuestion = (): number => {
    // First, look for skipped questions after current index
    for (let i = currentIndex + 1; i < totalWords; i++) {
      if (skippedIndices.includes(i) && !attemptedIndices.includes(i)) {
        return i;
      }
    }
    // Then look for unattempted questions after current index
    for (let i = currentIndex + 1; i < totalWords; i++) {
      if (!attemptedIndices.includes(i) && !skippedIndices.includes(i)) {
        return i;
      }
    }
    // If no skipped or unattempted questions after current, check from beginning for skipped
    for (let i = 0; i < currentIndex; i++) {
      if (skippedIndices.includes(i) && !attemptedIndices.includes(i)) {
        return i;
      }
    }
    // Then check for unattempted questions from beginning
    for (let i = 0; i < currentIndex; i++) {
      if (!attemptedIndices.includes(i) && !skippedIndices.includes(i)) {
        return i;
      }
    }
    return -1; // No unattempted or skipped questions found
  };

  // Start recording
  const handleStartRecording = async () => {
    try {
      setRecordingError(null);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Try to use WAV if supported, otherwise fallback to webm
      let mimeType = 'audio/webm;codecs=opus';
      if (MediaRecorder.isTypeSupported('audio/wav')) {
        mimeType = 'audio/wav';
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        mimeType = 'audio/webm';
      }
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      const sentenceId = currentSentence.sentence_id;

      mediaRecorder.onstop = () => {
        // Hold the encode in a promise so handleNext and submit can await it.
        // This used to be a fire-and-forget FileReader callback, so a child who
        // tapped Stop then Next immediately submitted an empty string and the
        // sentence was scored as unattempted.
        setIsProcessingAudio(true);
        encodingRef.current = (async () => {
          try {
            const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
            const audioContext = new AudioContext();
            const arrayBuffer = await audioBlob.arrayBuffer();
            const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

            // Time to speak: the first sample above the noise floor. Measured
            // from the decoded buffer so it is the same audio the server sees.
            const channel = audioBuffer.getChannelData(0);
            let firstSound = -1;
            for (let i = 0; i < channel.length; i++) {
              if (Math.abs(channel[i]) > 0.02) { firstSound = i; break; }
            }
            const speakMs = firstSound < 0
              ? null
              : Math.round((firstSound / audioBuffer.sampleRate) * 1000);

            const check = inspectAudioBuffer(audioBuffer);
            if (!check.ok) {
              setRecordingError(check.message);
              setRecordings((prev) => {
                const next = new Map(prev);
                next.delete(sentenceId);
                return next;
              });
              return;
            }

            const wavBlob = await audioBufferToWav(audioBuffer);
            const base64Data = await blobToBase64(wavBlob);
            if (!base64Data) {
              setRecordingError('The recording could not be saved. Please try again.');
              return;
            }

            setRecordingError(null);
            setRecordings((prev) => {
              const next = new Map(prev);
              next.set(sentenceId, base64Data);
              return next;
            });
            setTimeToSpeak((prev) => {
              const next = new Map(prev);
              if (speakMs !== null) next.set(sentenceId, speakMs);
              return next;
            });
            setAttempts((prev) => {
              const next = new Map(prev);
              next.set(sentenceId, (next.get(sentenceId) ?? 0) + 1);
              return next;
            });
          } catch (conversionError) {
            console.error('Error converting to WAV:', conversionError);
            setRecordingError('Failed to process the recording. Please try again.');
          } finally {
            setIsProcessingAudio(false);
          }
        })();

        stream.getTracks().forEach((track) => track.stop());
      };
      
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recording:', error);
      alert('Failed to access microphone. Please allow microphone access.');
    }
  };

  const blobToBase64 = (blob: Blob): Promise<string> =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        resolve(result ? result.split(',')[1] ?? '' : '');
      };
      reader.onerror = () => resolve('');
      reader.readAsDataURL(blob);
    });

  // Does the recording contain speech? Mirrors the server-side audio gate,
  // including its shape test: loudness alone cannot separate a quiet child
  // from a quiet room, because room tone at 5% is louder than speech at 1%.
  const inspectAudioBuffer = (
    buffer: AudioBuffer
  ): { ok: boolean; message: string } => {
    if (buffer.duration < MIN_RECORDING_SECONDS) {
      return {
        ok: false,
        message: 'That was too short. Hold the button and read the whole sentence.',
      };
    }

    const channel = buffer.getChannelData(0);
    const window = Math.max(1, Math.floor(buffer.sampleRate * FRAME_SECONDS));
    const frames: number[] = [];
    for (let start = 0; start + window <= channel.length; start += window) {
      let sum = 0;
      for (let i = start; i < start + window; i++) sum += channel[i] * channel[i];
      frames.push(Math.sqrt(sum / window));
    }
    if (frames.length === 0) {
      return {
        ok: false,
        message: 'That was too short. Hold the button and read the whole sentence.',
      };
    }

    const ordered = [...frames].sort((a, b) => a - b);
    const quiet = ordered[Math.floor(ordered.length * 0.1)];
    const loud = ordered[Math.floor(ordered.length * 0.9)];
    const dynamicRange = quiet > 1e-9 ? loud / quiet : Infinity;
    const voicedCut = loud * VOICED_RELATIVE_TO_PEAK;
    const voiced = frames.filter((f) => f >= voicedCut).length / frames.length;

    if (loud < MIN_PEAK_FRAME_RMS) {
      return {
        ok: false,
        message:
          'We could not hear anything. Check the microphone, move a little closer, and try again.',
      };
    }
    if (dynamicRange < MIN_DYNAMIC_RANGE || voiced < MIN_VOICED_FRACTION) {
      return {
        ok: false,
        message:
          'We heard background noise but no reading. Find a quieter spot and read the sentence aloud.',
      };
    }
    return { ok: true, message: '' };
  };

  // Helper function to convert AudioBuffer to WAV format
  const audioBufferToWav = async (audioBuffer: AudioBuffer): Promise<Blob> => {
    const numberOfChannels = audioBuffer.numberOfChannels;
    const sampleRate = audioBuffer.sampleRate;
    const format = 1; // PCM
    const bitDepth = 16;
    
    const bytesPerSample = bitDepth / 8;
    const blockAlign = numberOfChannels * bytesPerSample;
    
    const data = [];
    for (let i = 0; i < numberOfChannels; i++) {
      data.push(audioBuffer.getChannelData(i));
    }
    
    const interleaved = interleave(data);
    const dataLength = interleaved.length * bytesPerSample;
    const buffer = new ArrayBuffer(44 + dataLength);
    const view = new DataView(buffer);
    
    // Write WAV header
    writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + dataLength, true);
    writeString(view, 8, 'WAVE');
    writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, format, true);
    view.setUint16(22, numberOfChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * blockAlign, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitDepth, true);
    writeString(view, 36, 'data');
    view.setUint32(40, dataLength, true);
    
    // Write audio data
    floatTo16BitPCM(view, 44, interleaved);
    
    return new Blob([view], { type: 'audio/wav' });
  };

  const interleave = (channelData: Float32Array[]): Float32Array => {
    const length = channelData[0].length;
    const numberOfChannels = channelData.length;
    const result = new Float32Array(length * numberOfChannels);
    
    let inputIndex = 0;
    for (let i = 0; i < length; i++) {
      for (let channel = 0; channel < numberOfChannels; channel++) {
        result[inputIndex++] = channelData[channel][i];
      }
    }
    return result;
  };

  const writeString = (view: DataView, offset: number, string: string): void => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };

  const floatTo16BitPCM = (view: DataView, offset: number, input: Float32Array): void => {
    for (let i = 0; i < input.length; i++, offset += 2) {
      const s = Math.max(-1, Math.min(1, input[i]));
      view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
    }
  };

  // Stop recording
  const handleStopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const handleNext = async () => {
    // Wait for any in-flight encode before deciding whether audio exists.
    if (encodingRef.current) {
      await encodingRef.current;
    }

    const audio = recordings.get(currentSentence.sentence_id);
    if (!audio) {
      alert(
        recordingError ||
          'Please record your voice before moving to the next sentence.'
      );
      return;
    }

    const timeSpent = Math.floor((Date.now() - startTime) / 1000);
    
    // Mark as attempted
    setAttemptedIndices((prev) =>
      prev.includes(currentIndex) ? prev : [...prev, currentIndex]
    );
    
    // If this was the last sentence, submit all recordings
    if (currentIndex === totalWords - 1) {
      await handleSubmitBatch();
      return;
    }

    // Otherwise, move to next sentence
    const nextIndex = findNextUnattemptedOrSkippedQuestion();
    if (nextIndex !== -1) {
      setCurrentIndex(nextIndex);
    } else {
      // All sentences done, submit batch
      await handleSubmitBatch();
    }
  };

  // Submit all recordings in batch
  const handleSubmitBatch = async () => {
    try {
      // A recording still being encoded is a recording. Wait for it.
      if (encodingRef.current) {
        await encodingRef.current;
      }
      setIsSubmitting(true);
      
      // Prepare submissions array - include ALL sentences, with empty audio for unattempted ones
      const submissions: SpeakingSubmission[] = sentences.map(sentence => {
        const audioData = recordings.get(sentence.sentence_id);
        return {
          sentence_id: sentence.sentence_id,
          original_sentence: sentence.sentence,
          audio_base64: audioData || '', // Empty string for unattempted sentences
          audio_format: 'wav',
          time_to_speak_ms: timeToSpeak.get(sentence.sentence_id) ?? null,
          attempt: attempts.get(sentence.sentence_id) ?? 0,
        };
      });
      
      // Submit to API
      await submitSpeakingBatch(childId, gradeName, submissions);
      
      // Pass empty results array - the assessment page will fetch speaking results separately
      const formattedResults: ResultRow[] = [{
        word: '',
        user_input: '',
        type: 'speaking',
        time: 0,
        hints_used: 0
      }];
      
      onComplete({ words: formattedResults });
    } catch (error) {
      console.error('Error submitting recordings:', error);
      alert('Failed to submit recordings. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleLeave = async () => {
    // If user has any recordings, submit them before leaving
    if (recordings.size > 0) {
      await handleSubmitBatch();
      return;
    }
    
    // Just go back without submitting
    onBackToDashboard();
  };

  const handleSkip = () => {
    // Mark as skipped
    const wasSkipped = skippedIndices.includes(currentIndex);
    if (!wasSkipped) {
      setSkippedIndices((prev) => [...prev, currentIndex]);
    }
    
    setIsComplete(false);
    
    // Check if this is the last sentence
    if (currentIndex === totalWords - 1) {
      // Find next unskipped sentence or finish
      const nextIndex = findNextUnattemptedOrSkippedQuestion();
      if (nextIndex !== -1) {
        setCurrentIndex(nextIndex);
      } else {
        // All done
        handleSubmitBatch();
      }
      return;
    }
    
    // Find next sentence
    const nextIndex = findNextUnattemptedOrSkippedQuestion();
    if (nextIndex !== -1) {
      setCurrentIndex(nextIndex);
    } else {
      handleSubmitBatch();
    }
  };

  const audioRef = useRef<HTMLAudioElement>(null);
  const manualPlayRef = useRef(false);

  // Helper to get valid audio src
  const getAudioSrc = (audio: string) => {
    if (!audio) return "";
    if (audio.startsWith("data:audio")) return audio;
    return `data:audio/mp3;base64,${audio}`;
  };

  // Setup audio refs when sentence changes (no auto-play for speaking test)
  useEffect(() => {
    // Reset manual play flag when new sentence loads
    manualPlayRef.current = false;
    
    if (currentSentence?.audio_base64 && audioRef.current) {
      // Create a new audio instance for this sentence
      const newAudio = new Audio(getAudioSrc(currentSentence.audio_base64));
      newAudio.style.display = 'none';
      newAudio.currentTime = 0;
      newAudio.playbackRate = 1;
      
      // Update the ref to the new audio
      audioRef.current = newAudio;
      
      // NO AUTO-PLAY for speaking test - user reads the sentence themselves
    }
  }, [currentSentence]);

  const handlePlaySentence = (rate = 1) => {
    // Mark as manual play BEFORE doing anything else
    manualPlayRef.current = true;
    
    if (audioRef.current && currentSentence) {
      try {
        // Simply reuse the existing audio element
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current.playbackRate = rate;
        
        // Remove any existing event listeners by cloning the node
        const currentAudio = audioRef.current;
        const newAudio = currentAudio.cloneNode(false) as HTMLAudioElement;
        newAudio.src = getAudioSrc(currentSentence.audio_base64);
        newAudio.playbackRate = rate;
        newAudio.style.display = 'none';
        
        // Update the ref to the new audio element
        audioRef.current = newAudio;
        
        // Play the audio
        newAudio.play().catch(() => {});
      } catch (e) {
        // Ultimate fallback - just use the current audio element as-is
        try {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
          audioRef.current.playbackRate = rate;
          audioRef.current.play().catch(() => {});
        } catch (fallbackError) {
          // ignore
        }
      }
    }
  };

  /* ----------------------------- JSX ----------------------------- */
  
  // Show loading state
  if (isLoadingSentences) {
    return (
      <div className="h-screen w-full bg-gradient-to-br from-peach-50 via-lavender-100 to-white flex font-[Nunito_Sans,sans-serif] items-center justify-center">
        <div className="text-center">
          <TestBadge test="speaking" size="lg" className="mb-4" />
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#F7B26A] border-t-transparent mb-4"></div>
          <p className="text-lg text-[#1F224B] font-semibold">Loading sentences...</p>
        </div>
      </div>
    );
  }
  
  // Show submitting state
  if (isSubmitting) {
    return (
      <div className="h-screen w-full bg-gradient-to-br from-peach-50 via-lavender-100 to-white flex font-[Nunito_Sans,sans-serif] items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#F7B26A] border-t-transparent mb-4"></div>
          <p className="text-lg text-[#1F224B] font-semibold">Submitting your recordings...</p>
          <p className="text-sm text-[#6B6FA3] mt-2">Please wait, this may take a moment</p>
        </div>
      </div>
    );
  }

  if (!currentSentence) {
    return (
      <div className="h-screen w-full bg-gradient-to-br from-peach-50 via-lavender-100 to-white flex font-[Nunito_Sans,sans-serif] items-center justify-center">
        <div className="text-center">
          <p className="text-lg text-[#1F224B] font-semibold">No sentences available</p>
          <Button onClick={onBackToDashboard} className="mt-4">
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-full bg-gradient-to-br from-peach-50 via-lavender-100 to-white flex font-[Nunito_Sans,sans-serif] flex-col">
      <div className="three-col-layout flex flex-1 gap-4 lg:gap-6 items-start unified-medium-scale">
        {/* Desktop: left question navigation */}
        <div className="left-col hidden lg:flex lg:flex-col lg:w-96 xl:w-[420px] p-4 flex-shrink-0 unified-medium-panel">
          <div className="sticky top-20">
            <div className="w-full p-4 rounded-2xl bg-gradient-to-b from-[#F3E8FF] to-white/80 shadow-md border border-gray-100 lg:origin-top-left unified-medium-inner">
              <QuestionPalette
                total={totalWords}
                currentIndex={currentIndex}
                attempted={attemptedIndices}
                skipped={skippedIndices}
                onJump={(idx) => { 
                  // Only allow navigation to skipped questions or current question
                  if (skippedIndices.includes(idx) || idx === currentIndex) {
                    setCurrentIndex(idx); 
                    window.scrollTo({ top: 0, behavior: 'smooth' }); 
                  }
                }}
                isMobile={false}
                isTablet={false}
                isDesktopLeft={true}
                collapsed={false}
              />
            </div>
          </div>
        </div>

        {/* Center Main Card */}
        <div className="center-col flex-1 flex flex-col items-center lg:origin-top-left min-w-0 unified-medium-panel">
          <div className="center-inner w-full max-w-2xl mx-auto mt-0 md:mt-4 p-2 md:p-4 unified-medium-inner flex flex-col flex-1">
            <div className="flex items-center justify-between mb-4">
              <Button
                onClick={onBackToDashboard}
                variant="outline"
                size="sm"
                className="bg-white/80 hover:bg-white"
              >
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <TestBadge test="speaking" subject={playerName} />
                  {isMobile && <div className="text-xs text-gray-600">Question {currentIndex + 1} of {totalWords}</div>}
                </div>
                
                {/* Mobile Menu Button */}
                <div className="relative lg:hidden">
                  <Button
                    onClick={() => setShowMobileMenu(!showMobileMenu)}
                    variant="outline"
                    size="sm"
                    className="bg-white/80 hover:bg-white p-2"
                  >
                    <Menu className="w-4 h-4" />
                  </Button>
                  
                  {/* Mobile Side Navigation */}
                  {showMobileMenu && (
                    <>
                      {/* Backdrop */}
                      <div 
                        className="fixed inset-0 bg-black/50 z-40 transition-opacity duration-300"
                        onClick={() => setShowMobileMenu(false)}
                      />
                      
                      {/* Side Navigation Panel */}
                      <div className={`fixed top-0 right-0 h-full w-80 bg-white z-50 transform transition-transform duration-300 ease-in-out shadow-2xl flex flex-col ${
                        showMobileMenu ? 'translate-x-0' : 'translate-x-full'
                      }`}>
                        {/* Header */}
                        <div className="bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] p-4 text-white flex-shrink-0">
                          <div className="flex items-center justify-between">
                            <h3 className="text-lg font-bold">Question Navigation</h3>
                            <button
                              onClick={() => setShowMobileMenu(false)}
                              className="p-2 rounded-full hover:bg-white/20 transition-colors"
                            >
                              <X className="w-5 h-5" />
                            </button>
                          </div>
                          <div className="mt-2 text-sm opacity-90">
                            Question {currentIndex + 1} of {totalWords}
                          </div>
                        </div>
                        
                        {/* End Test Button */}
                        <div className="p-4 border-b border-gray-100">
                          <button 
                            onClick={() => {
                              handleLeave();
                              setShowMobileMenu(false);
                            }} 
                            className="w-full py-3 rounded-xl text-white font-bold shadow-md bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] hover:brightness-95 transition-all duration-200 flex items-center justify-center gap-2"
                          >
                            <span>🏁</span>
                            <span>End Test</span>
                          </button>
                        </div>

                        {/* Scrollable Content */}
                        <div className="flex-1 overflow-y-auto p-4" style={{ maxHeight: 'calc(100vh - 200px)' }}>
                          <div className="bg-gradient-to-b from-[#F3E8FF] to-white/80 rounded-xl p-4 shadow-sm">
                            <QuestionPalette
                              total={totalWords}
                              currentIndex={currentIndex}
                              attempted={attemptedIndices}
                              skipped={skippedIndices}
                              onJump={(idx) => { 
                                if (skippedIndices.includes(idx) || idx === currentIndex) {
                                  setCurrentIndex(idx); 
                                  setShowMobileMenu(false);
                                  window.scrollTo({ top: 0, behavior: 'smooth' }); 
                                }
                              }}
                              isMobile={true}
                              isTablet={false}
                              isDesktopLeft={false}
                              collapsed={false}
                            />
                          </div>
                          
                          {/* Additional Info Section */}
                          <div className="mt-6 space-y-4">
                            <div className="bg-blue-50 rounded-xl p-4">
                              <h4 className="font-semibold text-[#55608A] mb-2">Test Progress</h4>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Completed:</span>
                                  <span className="font-medium text-blue-600">{attemptedIndices.length} / {totalWords}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Current Time:</span>
                                  <span className="font-medium text-blue-600">{currentWordTime}s</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Grade Level:</span>
                                  <span className="font-medium text-blue-600">{gradeName}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            
            <Card className="p-4 sm:p-6 rounded-3xl shadow-xl bg-white/95 flex flex-col items-center pb-4 h-full overflow-y-auto">
              {/* Progress bar + motivational message */}
              <div className="w-full mb-2">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-xs sm:text-sm text-[#6B6FA3]">Question {currentIndex + 1} of {totalWords}</div>
                  <div className="flex items-center gap-2 sm:gap-4">
                    <div className="text-xs sm:text-sm font-semibold text-[#55608A] hidden sm:block">{currentMotivation}</div>
                    <div className="text-xs sm:text-sm font-bold text-[#8E7BFF] bg-white/80 px-2 py-0.5 rounded-full border border-[#EDE9FF]">
                      Time: {currentWordTime}s
                    </div>
                  </div>
                </div>
                <div
                  className="w-full h-3 rounded-full overflow-hidden shadow-sm relative"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round(progressPercentage)}
                  aria-label={`Progress: ${Math.round(progressPercentage)}%`}
                >
                  <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-[#F3E8FF] via-[#FFF7EE] to-[#F8FBFF] opacity-95 z-0" />
                  <div
                    style={{ width: `${progressPercentage}%` }}
                    className="absolute left-0 top-0 h-full rounded-full bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] shadow-md transition-all z-10"
                  />
                </div>
              </div>
              
              {/* Hidden audio elements */}
              <audio
                ref={audioRef}
                style={{ display: 'none' }}
                src={getAudioSrc(currentSentence.audio_base64)}
                key={currentSentence.sentence_id}
              />
              
              {/* Speaking Test Content */}
              <div className="mb-2 sm:mb-4">
                <div className="flex items-center justify-center">
                  <div className="w-[5rem] h-[5rem] sm:w-[6rem] sm:h-[6rem] rounded-full overflow-hidden shadow-md border-2 border-gray-200">
                    <img src={login_hero_image} alt="Speaking Test Icon" className="w-full h-full object-cover" />
                  </div>
                </div>
              </div>
              
              <div className="mb-3 sm:mb-4 text-center">
                <div className="text-base sm:text-xl font-bold text-[#8E7BFF] mb-2">Read and speak this sentence:</div>
                
                {/* Kindergarten helper hint */}
                {gradeName === 'Kindergarten' && (
                  <div className="mb-3 mx-auto max-w-md rounded-xl border-2 border-[#F7B26A]/40 bg-[#FFF7EE] px-4 py-2 text-sm text-[#8A5A2B] flex items-center justify-center gap-2">
                    <span className="text-lg">🤝</span>
                    <span>If reading feels tricky, you can take your parent's help.</span>
                  </div>
                )}
                
                {/* Display the sentence prominently */}
                <div className="bg-gradient-to-br from-[#F3E8FF] to-[#FFF7EE] rounded-2xl p-3 sm:p-4 shadow-lg border-2 border-[#EDE9FF]">
                  <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-[#F7B26A] leading-snug">
                    {currentSentence.sentence}
                  </div>
                  <div className="flex items-center justify-center gap-3 mt-2 text-xs sm:text-sm text-[#6B6FA3]">
                    <span className="bg-white px-2 py-0.5 rounded-full">
                      {currentSentence.word_count} words
                    </span>
                    <span className="bg-white px-2 py-0.5 rounded-full capitalize">
                      {currentSentence.difficulty}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Recording problems must be visible, not silent zeroes */}
              {recordingError && !isRecording && (
                <div
                  role="alert"
                  className="mb-3 mx-auto max-w-md rounded-xl border-2 border-[#F7B26A] bg-[#FFF7EE] px-4 py-3 text-center text-sm text-[#8A5A2B]"
                >
                  {recordingError}
                </div>
              )}
              {isProcessingAudio && (
                <div className="mb-3 text-center text-sm text-[#6B6FA3]">
                  Saving your recording...
                </div>
              )}

              {/* Microphone Button - Start/Stop Recording */}
              <div className="flex flex-col items-center justify-center mb-3 sm:mb-4">
                {!isRecording ? (
                  <button 
                    onClick={handleStartRecording}
                    className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-[#F7B26A] to-[#ECA7C1] text-white flex items-center justify-center shadow-2xl hover:brightness-95 hover:scale-105 active:scale-95 transition-all duration-200"
                    aria-label="Start recording"
                  >
                    <Mic className="w-8 h-8 sm:w-10 sm:h-10" />
                  </button>
                ) : (
                  <button 
                    onClick={handleStopRecording}
                    className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-red-500 to-red-600 text-white flex items-center justify-center shadow-2xl hover:brightness-95 hover:scale-105 active:scale-95 transition-all duration-200 animate-pulse"
                    aria-label="Stop recording"
                  >
                    <Square className="w-8 h-8 sm:w-10 sm:h-10" fill="currentColor" />
                  </button>
                )}
                
                <div className="mt-2 text-center">
                  {isRecording ? (
                    <p className="text-sm text-red-600 font-bold animate-pulse">🔴 Recording... Click to stop</p>
                  ) : recordings.has(currentSentence.sentence_id) ? (
                    <p className="text-sm text-green-600 font-semibold">✅ Recording saved! Click Next to continue</p>
                  ) : (
                    <p className="text-sm text-[#6B6FA3] italic">Click the microphone to start recording</p>
                  )}
                </div>
              </div>
              
              {/* Action buttons */}
              <div className="flex gap-2 sm:gap-3 justify-center w-full mb-2 flex-wrap">
                <Button
                  onClick={handleNext}
                  disabled={isRecording || isProcessingAudio || !recordings.has(currentSentence.sentence_id)}
                  className={`min-w-[6rem] sm:min-w-[8rem] h-10 sm:h-12 rounded-full text-sm sm:text-lg font-bold shadow-lg transition-all ${
                    isRecording || !recordings.has(currentSentence.sentence_id)
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'text-white bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] hover:brightness-95'
                  }`}
                >
                  {currentIndex === totalWords - 1 ? 'Submit →' : 'Next →'}
                </Button>
                <Button
                  onClick={handleSkip}
                  size="lg"
                  variant="outline"
                  disabled={isRecording}
                  className="min-w-[5rem] sm:min-w-[6.5rem] h-10 sm:h-12 rounded-full text-sm sm:text-lg font-semibold border-2 border-[#EDE9FF] text-[#6B6FA3] bg-white"
                >
                  Skip
                </Button>
              </div>
              
              {/* Helpful message */}
              <div className="text-center">
                <p className="text-xs sm:text-sm text-[#6B6FA3] bg-[#F6F6FF] border border-[#EDE9FF] rounded-xl px-3 py-1.5 inline-block">
                  💡 {isRecording 
                    ? 'Recording in progress...' 
                    : recordings.has(currentSentence.sentence_id)
                    ? 'Recording saved! Click Next to continue'
                    : 'Click the microphone to start recording'}
                </p>
                <p className="text-xs text-[#6B6FA3] mt-1">
                  Progress: {recordings.size} / {totalWords} recorded
                </p>
              </div>
            </Card>
          </div>
        </div>

        {/* Right Sidebar: Speaking Instructions */}
        <div className="right-col hidden lg:flex flex-col p-6 lg:p-8 bg-gradient-to-b from-blue-50 via-white to-blue-100 rounded-xl shadow-lg min-h-[500px] max-h-full overflow-auto border border-blue-200 lg:w-96 xl:w-[520px] lg:origin-top-right flex-shrink-0 unified-medium-panel" style={{ marginTop: '15px' }}>
          <div className="unified-medium-inner">
            <div className="mt-6 lg:mt-0 sticky top-20 w-full">
              <div className="w-full flex flex-col gap-4 lg:gap-6">
                <h2 className="font-bold text-xl lg:text-2xl mb-4 text-[#55608A]">Read Aloud Instructions</h2>

                {/* Kindergarten parent-help note */}
                {gradeName === 'Kindergarten' && (
                  <div className="mb-4 rounded-2xl border-2 border-[#F7B26A]/40 bg-gradient-to-br from-[#FFF7EE] to-white p-4 shadow-sm">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">🤝</span>
                      <div>
                        <h3 className="font-bold text-[#55608A] mb-1">Parent Help</h3>
                        <p className="text-sm text-[#6B6FA3]">If reading feels tricky, you can take your parent's help.</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Instructions Card */}
                <div className="bg-gradient-to-br from-[#F3E8FF] to-white p-6 rounded-2xl border-2 border-[#EDE9FF] shadow-sm">
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">👀</span>
                      <div>
                        <h3 className="font-bold text-[#55608A] mb-1">Read the Sentence</h3>
                        <p className="text-sm text-[#6B6FA3]">Look at the sentence displayed on the screen</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">🎤</span>
                      <div>
                        <h3 className="font-bold text-[#55608A] mb-1">Click Microphone</h3>
                        <p className="text-sm text-[#6B6FA3]">Press the microphone button to start recording</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">🗣️</span>
                      <div>
                        <h3 className="font-bold text-[#55608A] mb-1">Speak Clearly</h3>
                        <p className="text-sm text-[#6B6FA3]">Say the sentence loud and clear</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">➡️</span>
                      <div>
                        <h3 className="font-bold text-[#55608A] mb-1">Move Forward</h3>
                        <p className="text-sm text-[#6B6FA3]">Click Next to go to the next sentence</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-3 text-base lg:text-lg mt-4 w-full">
                  <div>
                    <span className="font-semibold text-gray-700 lg:text-lg">Current Question</span><br />
                    <span className="text-blue-600 font-bold lg:text-lg">{currentIndex + 1} of {totalWords}</span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-700 lg:text-lg">Grade Level</span><br />
                    <span className="text-orange-600 font-bold lg:text-lg">{gradeName}</span>
                  </div>
                </div>
                <button onClick={handleLeave} className="w-full mb-2 py-3 lg:py-5 rounded-lg text-white text-base lg:text-xl lg:font-extrabold shadow transition" style={{ background: 'linear-gradient(90deg,#F7B26A,#ECA7C1)' }}>End Test</button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <footer style={{ marginTop: 10, color: '#6B6FA3', fontSize: 12, textAlign: 'center' }}>© The Dear Parent Project</footer>
    </div>
  );
};
